import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Named ProductCard variants → representative swatch color (mirrors the
// dashboard's ProductCard palette in constants.ts). `variant` stored in
// ProductRegionConfig.metadata.card is either one of these names or a hex.
const VARIANT_COLORS: Record<string, string> = {
  yellow: "#FFBF00",
  blue: "#0053FF",
  magenta: "#CE0574",
  purple: "#7300FF",
  teal: "#0484A4",
  fuchsia: "#C20BA6",
  "forest-green": "#324D38",
  mauve: "#735754",
  slate: "#2E6172",
};

const LIGHT = "#F5F5F5"; // 'light' / default card background

function Swatch({ color }: { color: string }) {
  return (
    <span
      className="inline-block h-4 w-4 rounded-sm border border-black/10 shrink-0"
      style={{ backgroundColor: color }}
    />
  );
}

/**
 * Card color selector: a dropdown of the named variants (each with a color
 * swatch) plus a "Custom…" option that reveals a native color picker + hex
 * input. `value` is "" (default/light), a variant name, or a hex string.
 */
export default function CardColorField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const isCustom = value.startsWith("#");
  const selectValue = isCustom ? "CUSTOM" : value || "DEFAULT";

  const handleSelect = (v: string) => {
    if (v === "DEFAULT") onChange("");
    else if (v === "CUSTOM") onChange(isCustom ? value : "#0053FF");
    else onChange(v);
  };

  return (
    <div className="space-y-2">
      <Select value={selectValue} onValueChange={handleSelect}>
        <SelectTrigger>
          <SelectValue placeholder="Default (light)" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="DEFAULT">
            <span className="flex items-center gap-2">
              <Swatch color={LIGHT} /> Default (light)
            </span>
          </SelectItem>
          {Object.entries(VARIANT_COLORS).map(([name, hex]) => (
            <SelectItem key={name} value={name}>
              <span className="flex items-center gap-2">
                <Swatch color={hex} /> {name}
              </span>
            </SelectItem>
          ))}
          <SelectItem value="CUSTOM">
            <span className="flex items-center gap-2">
              <span
                className="inline-block h-4 w-4 rounded-sm border border-black/10 shrink-0"
                style={{
                  background:
                    "conic-gradient(red, orange, yellow, lime, aqua, blue, magenta, red)",
                }}
              />
              Custom…
            </span>
          </SelectItem>
        </SelectContent>
      </Select>

      {isCustom && (
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#0053FF"}
            onChange={(e) => onChange(e.target.value)}
            className="h-9 w-12 rounded border border-gray-200 p-0.5 cursor-pointer"
            aria-label="Custom card color"
          />
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="#RRGGBB"
            className="w-32 h-9 rounded-md border border-gray-200 px-2 text-sm font-mono"
          />
        </div>
      )}
    </div>
  );
}
